import { AutomationHealth } from './automations-list-v2/automation-health';

export class Automation {
  id?: string;
  siteId?: number;
  isDisabled?: boolean;
  name?: string;
  status?: string;
  createdDate?: string;
  updatedDate?: string | null;
  lastModifedByUserEmail?: string;
  isFormTrigger?: boolean;
  // Client-derived (never sent by the API): true for oc_action_ campaign automations; stamped post-fetch.
  isComponentTrigger?: boolean;
  triggers?: TriggerCondition[];
  actions?: AutomationAction[];
  // Display-only, set on component-interaction automations so the list can name them without a widget lookup.
  sourceType?: AutomationSourceType;
  sourceLabel?: string;
  // Display-only, set after fetch for the v2 list.
  displayName?: string;
  triggerLabel?: string;
  triggerLabels?: string[];
  triggerTooltip?: string;
  triggerIcon?: string;
  experienceName?: string;
  experienceVariant?: string;
  experienceLabel?: string;
  experienceThumbnail?: string;
  widgetEditorUrl?: string;
  health?: AutomationHealth;
  runCount?: number;
  lastRun?: Date;
  latestErrorMessage?: string;
  created?: Date;
  modified?: Date;
  modifiedBy?: string;
  disabledErrorMessageKey?: AutomationErrorType;
  widgetId?: number;
}

export class AutomationEditorConfig {
  actionsConfig: ActionConfig[];
  name: string;
  iconUrl: string;
  expanded: boolean;
  enabled: boolean;
  isDraft?: boolean;
  inputFieldMappings?: any;
  isAuthenticated?: boolean;
  errorKey?: string;
  multi?: boolean;
  type?: string;
  isRunningInRealTime?: boolean;
  isFormMappingProp?: boolean;
  displayName?: string;
}

export class ActionConfig {
  actionsGroup: ActionGroup[];
  nameGroup: string;
  requiredList?: boolean;
  expanded: boolean;
  isZapierEmbed: boolean;
  isZapierTrigger: boolean;
  disabled?: boolean;
  additionalInfoKey?: string;
}

export class ActionGroup {
  action: CustomModelAction;
}

export class AutomationAction {
  $type: string;
  properties?: string[];
  isRunningInRealTime?: boolean;
  name?: string;
  fieldMappings?: any;
  fieldValue?: string;
  fieldName?: string;
}

export class CustomModelAction {
  $type: string;
  nameAction: string;
  translationKey: string;
  properties: any[];
  listId?: string;
  listIds?: string;
  tagId?: string | string[];
  tagIds?: string[];
  requiredList?: boolean;
  isRunningInRealTime?: boolean;
  automationHasActionRealTime?: boolean;
  fieldMappings?: any;
  hasFieldsMappingAction?: boolean;
}

export class CustomModelProperty {
  description: string;
  listUI: any[];
  name: string;
  required: boolean;
  translationKey: string;
  typeInput: string;
  url: string;
  value: any;
  requiredInFormSettings?: boolean;
}

// Mirrors the backend automation SourceType; string-valued so it round-trips the API verbatim.
export enum AutomationSourceType {
  Button = 'Button',
  ButtonList = 'ButtonList',
  Image = 'Image',
  Text = 'Text',
  StepNavigation = 'StepNavigation',
  State = 'State',
}

/** An event seen in traffic; the catalogue the Event trigger picker is built from. */
export interface EventDefinition {
  id: string;
  type: string;
  widgetId: number;
  componentId: number;
}

/** One row in the Coupons trigger picker. */
export interface EventScopeOption {
  value: string;
  label: string;
}

/** A coupon component that can be targeted, whether or not any traffic has seen it yet. */
export interface CouponComponent {
  widgetId: number;
  componentId: number;
  widgetName?: string;
  campaignName?: string;
  couponSetId?: string;
  code?: string;
}

export class TriggerCondition {
  argument: string;
  operator?: string;
  type: string;
  /** Persisted discriminator within `type`; part of a trigger's identity alongside it. */
  subType?: string;
  /** Event scope. Null widget means any widget; null component means any component in the widget. */
  widgetId?: number;
  componentId?: number;
  isReadOnly?: boolean;
  arguments?: any;
  name?: string;
  default?: boolean;
  typeArgument?: string;
}

export enum TypeInput {
  List = 'list',
  Number = 'number',
  Text = 'text',
  TextArea = 'textArea',
  Switch = 'switch',
  ListSelect = 'list-select',
  ListMultiSelect = 'list-multi-select',
  Description = 'description',
  /**
   * Repeating-row mapping editor: each row has a source-field dropdown (from `sourceOptions`)
   * and a destination text input. Bound value is a Dictionary<string, string> where the key
   * is the source field name and the value is the destination (typically a dot-notation path).
   */
  FieldMappingDictionary = 'field-mapping-dictionary',
}

export enum TypeStep {
  AddContact = 'Add Contact',
  FieldMapping = 'Field Mapping',
  UpdateOtherFields = 'Update other fields and tags',
  Trigger = 'Trigger an automation',
  Webhook = 'Webhook',
  Email = 'Email',
  TagRemove = 'Remove tag',
  TagAdd = 'Add tag',
  SendEmail = 'Send an email',
  ZapierEmbed = 'Manage your Zapier Zaps',
}

export class AutomationHistory {
  public executedTime: Date;
  public actionTranslationKey: string;
  public errorMessageKey?: AutomationErrorType;
  public visitorEmail: string;
  public visitorSessionID: number;
  public triggerHistoryID: string;
  public contactId: string;
}

export enum AutomationErrorType {
  NoEmailAddressFound,
  MissingArgument,
  SendEmailUnexpectedError,
  UnexpectedError,
  SubscriberNotFound,
  NotImplemented,
  InvalidEmailAddress,
}

export type AutomationRunOutcomeResponse = 'Success' | 'Failed' | 'Waiting' | 'Aborted' | 'Warning';

export interface AutomationRecentRunResponse {
  executionTime: string;
  outcome: AutomationRunOutcomeResponse;
}

export class AutomationStatusResponse {
  public automationId: string;
  public newestExecutionTime: Date;
  public executionCount: number;
  /** Newest first; only sent when asked for with recentRuns. */
  public recentRuns?: AutomationRecentRunResponse[];
  public latestErrorMessage?: string;
}

export interface CrossSiteCopyAutomationResponse {
  id: string;
  siteId: number;
  name: string;
  isDisabled: boolean;
}

export interface CrossSiteCopyAutomationError {
  code?: string;
  missingIntegrations?: string[];
  message?: string;
}
